import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { PageHeader, Badge, Button, Field, Input, Select, Textarea, EmptyState } from '../components/ui';
import { FormError } from '../components/forms/common';
import { useAuth } from '../stores/auth';
import { templatesApi, type EmailTemplate } from '../lib/comms';

type TenancyLite = { id: string; reference: string; unit?: { reference?: string | null } | null; property?: { name?: string | null } | null };

/**
 * The letters the platform writes (prototype §37). Edit one and every future letter uses
 * your words; anything in {{double.braces}} is filled from the tenancy when it is written.
 */
export default function TemplatesPage() {
  const tpls = useQuery({ queryKey: ['/templates'], queryFn: templatesApi.list });
  const [key, setKey] = useState<string | null>(null);
  useEffect(() => { if (!key && tpls.data?.length) setKey(tpls.data[0]!.key); }, [tpls.data, key]);
  const current = tpls.data?.find((t) => t.key === key) ?? null;

  return (
    <div>
      <PageHeader title="Templates" />
      {tpls.isLoading ? (
        <div className="surface h-64 animate-pulse" />
      ) : tpls.error ? (
        <EmptyState icon="TriangleAlert" title="Could not load the templates" body={(tpls.error as Error).message} />
      ) : (
        <div className="grid gap-2.5 lg:grid-cols-[260px_1fr]">
          <div className="lg:hidden">
            <Select value={key ?? ''} onChange={(e) => setKey(e.target.value)} className="w-full" aria-label="Template">
              {tpls.data?.map((t) => <option key={t.key} value={t.key}>{t.name}{t.edited ? ' (edited)' : ''}</option>)}
            </Select>
          </div>
          <div className="surface hidden divide-y divide-ink-100 self-start overflow-hidden lg:block">
            {tpls.data?.map((t) => (
              <button key={t.key} onClick={() => setKey(t.key)} className={`block w-full px-4 py-2.5 text-left transition ${t.key === key ? 'bg-bronze-50' : 'hover:bg-ink-50/60'}`}
                style={{ boxShadow: t.key === key ? 'inset 3px 0 0 var(--color-bronze-500)' : undefined }}>
                <div className="flex items-center gap-2 text-[13px] font-medium text-ink-900">{t.name}{t.edited && <span className="rounded-full bg-amber-50 px-1.5 py-[1px] text-[10.5px] font-medium text-amber-700">edited</span>}</div>
                <div className="mt-0.5 line-clamp-2 text-[11.5px] text-ink-500">{t.description}</div>
              </button>
            ))}
          </div>
          {current && <Editor key={current.key + current.updatedAt} tpl={current} />}
        </div>
      )}
    </div>
  );
}

function Editor({ tpl }: { tpl: EmailTemplate }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canEdit = can('templates.update');
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [sms, setSms] = useState(tpl.smsBody ?? '');
  const [tenancyId, setTenancyId] = useState('');
  const [debounced, setDebounced] = useState({ subject: tpl.subject, body: tpl.body });
  const [sheetOpen, setSheetOpen] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFocus = useRef<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);
  useEffect(() => { const t = setTimeout(() => setDebounced({ subject, body }), 350); return () => clearTimeout(t); }, [subject, body]);
  const dirty = subject !== tpl.subject || body !== tpl.body || (tpl.smsBody !== null && sms !== tpl.smsBody);

  const fields = useQuery({ queryKey: ['/templates/fields'], queryFn: templatesApi.fields, staleTime: Infinity });
  const tenancies = useQuery({
    queryKey: ['/tenancies', 'template-sample'], enabled: can('tenancies.read'), staleTime: 5 * 60_000,
    queryFn: async () => (await api.get<TenancyLite[]>('/tenancies', { status: 'ACTIVE', perPage: 50 })).data,
  });
  const preview = useQuery({
    queryKey: ['/templates/render', tpl.key, debounced, tenancyId],
    queryFn: () => templatesApi.render({ subject: debounced.subject, body: debounced.body, tenancyId: tenancyId || null }),
    placeholderData: (prev) => prev,
  });

  const save = useMutation({ mutationFn: () => templatesApi.update(tpl.key, { subject, body, ...(tpl.smsBody !== null ? { smsBody: sms } : {}) }), onSuccess: () => qc.invalidateQueries({ queryKey: ['/templates'] }) });
  const reset = useMutation({ mutationFn: () => templatesApi.reset(tpl.key), onSuccess: () => qc.invalidateQueries({ queryKey: ['/templates'] }) });

  /** Put {{field}} where the cursor was. */
  const insert = (field: string) => {
    const token = `{{${field}}}`;
    if (lastFocus.current === 'subject' && subjectRef.current) {
      const el = subjectRef.current; const at = el.selectionStart ?? subject.length;
      setSubject(subject.slice(0, at) + token + subject.slice(el.selectionEnd ?? at));
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(at + token.length, at + token.length); });
      return;
    }
    const el = bodyRef.current; const at = el?.selectionStart ?? body.length;
    setBody(body.slice(0, at) + token + body.slice(el?.selectionEnd ?? at));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(at + token.length, at + token.length); });
  };

  return (
    <div className="grid min-w-0 gap-2.5 xl:grid-cols-2">
      <div className="surface p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="flex-1 text-[15px] font-semibold text-ink-900">{tpl.name}</h2>
          {tpl.team && <Badge value={tpl.team} tone="neutral" />}
          {tpl.edited && <Badge value="Edited" tone="warning" />}
        </div>
        <div className="space-y-3">
          <Field label="Subject"><Input ref={subjectRef} value={subject} onChange={(e) => setSubject(e.target.value)} onFocus={() => { lastFocus.current = 'subject'; }} disabled={!canEdit} className="w-full" /></Field>
          <Field label="The letter">
            <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} onFocus={() => { lastFocus.current = 'body'; }} disabled={!canEdit} rows={18}
              className="w-full font-mono text-[12.4px] leading-relaxed" spellCheck={false} />
          </Field>
          {tpl.smsBody !== null && (
            <Field label={`Text message version (${sms.length} characters${sms.length > 160 ? ` — ${Math.ceil(sms.length / 153)} texts` : ''})`} hint="Used when this goes as an SMS: a contractor booked or on the way, a viewing reminder.">
              <Textarea value={sms} onChange={(e) => setSms(e.target.value)} disabled={!canEdit} rows={3} className="w-full font-mono text-[12.4px]" spellCheck={false} />
            </Field>
          )}
        </div>

        <div className="mt-3 rounded-lg border border-ink-200">
          <button onClick={() => setSheetOpen((v) => !v)} className="flex w-full items-center justify-between px-3.5 py-2.5 text-[12.5px] font-medium text-ink-700">
            Merge fields — click one to put it where your cursor is
            <ChevronDown className={`h-4 w-4 transition ${sheetOpen ? 'rotate-180' : ''}`} />
          </button>
          {sheetOpen && fields.data && (
            <div className="max-h-72 space-y-3 overflow-y-auto border-t border-ink-100 px-3.5 py-3">
              {fields.data.map((g) => (
                <div key={g.group}>
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">{g.group}</div>
                  <div className="space-y-1">
                    {g.fields.map((f) => (
                      <button key={f.key} disabled={!canEdit} onClick={() => insert(f.key)} className="flex w-full flex-wrap items-baseline gap-x-2 rounded-lg px-1.5 py-1 text-left hover:bg-ink-50 disabled:hover:bg-transparent">
                        <code className="text-[11.5px] text-bronze-700">{`{{${f.key}}}`}</code>
                        <span className="text-[11.5px] text-ink-500">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
            {dirty && <Button variant="ghost" onClick={() => { setSubject(tpl.subject); setBody(tpl.body); setSms(tpl.smsBody ?? ''); }}>Discard changes</Button>}
            <div className="flex-1" />
            {tpl.isSystem && tpl.edited && !dirty && <Button variant="secondary" icon="RotateCcw" onClick={() => reset.mutate()} disabled={reset.isPending}>Back to the standard wording</Button>}
          </div>
        )}
        {(save.error || reset.error) && <div className="mt-3"><FormError error={save.error ?? reset.error} /></div>}
      </div>

      <div className="surface self-start p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="flex-1 text-[13px] font-semibold text-ink-900">Preview</h3>
          {preview.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
          {tenancies.data && tenancies.data.length > 0 && (
            <Select value={tenancyId} onChange={(e) => setTenancyId(e.target.value)} className="w-full !py-1.5 text-[12px] sm:w-64" aria-label="Sample tenancy">
              <option value="">A sample tenancy</option>
              {tenancies.data.map((t) => <option key={t.id} value={t.id}>{[t.unit?.reference, t.property?.name].filter(Boolean).join(', ') || t.reference} — {t.reference}</option>)}
            </Select>
          )}
        </div>
        {preview.data?.sample.label && <p className="mb-3 text-[11.5px] text-ink-400">Filled from {preview.data.sample.label}.</p>}
        {preview.error ? <FormError error={preview.error} /> : preview.data && (
          <>
            {preview.data.missing.length > 0 && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                Not filled for this tenancy: {preview.data.missing.map((m) => `{{${m}}}`).join(', ')}. A letter written with them left in waits in the outbox as a draft until someone fills them.
              </div>
            )}
            <div className="overflow-hidden rounded-lg border border-ink-200">
              <div className="border-b border-ink-100 bg-ink-50 px-4 py-2.5 text-[13px] font-medium text-ink-900">{preview.data.subject}</div>
              <pre className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words bg-white px-4 py-4 font-sans text-[13px] leading-relaxed text-ink-800">{preview.data.body}</pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
